/**
 * Telegram bot-messages + FAQ API client
 * (BFF: `/api/v1/store/settings/telegram/messages`, epic-002).
 *
 * Thin passthrough to `web-content-service`'s bot-content aggregate. `locale` is
 * kept as a parameter throughout (the contract is multi-locale from day one) even
 * though v1 of the merchant-portal editor only ever calls it with `'ru'` — there
 * is no locale switcher UI yet.
 */
import { apiClient } from '@/shared/api';
import type {
  BotMessage,
  BotMessageList,
  CreateFaqRequest,
  FaqEntry,
  FaqEntryList,
  UpdateFaqRequest,
  UpsertBotMessageRequest,
} from '../model/settingsTypes';

const BASE = '/api/v1/store/settings/telegram/messages';

/** Default content locale — the only locale the v1 editor UI offers. */
export const DEFAULT_BOT_MESSAGE_LOCALE = 'ru';

export const listBotMessages = async (
  locale: string = DEFAULT_BOT_MESSAGE_LOCALE,
): Promise<BotMessageList> => (await apiClient.get<BotMessageList>(BASE, { params: { locale } })).data;

export const upsertBotMessage = async (
  contentKey: string,
  body: UpsertBotMessageRequest,
): Promise<BotMessage> =>
  (await apiClient.put<BotMessage>(`${BASE}/${encodeURIComponent(contentKey)}`, body)).data;

export const resetBotMessage = async (
  contentKey: string,
  locale: string = DEFAULT_BOT_MESSAGE_LOCALE,
): Promise<void> => {
  await apiClient.delete(`${BASE}/${encodeURIComponent(contentKey)}`, { params: { locale } });
};

export const listFaqEntries = async (
  locale: string = DEFAULT_BOT_MESSAGE_LOCALE,
): Promise<FaqEntryList> =>
  (await apiClient.get<FaqEntryList>(`${BASE}/faq`, { params: { locale } })).data;

export const createFaqEntry = async (body: CreateFaqRequest): Promise<FaqEntry> =>
  (await apiClient.post<FaqEntry>(`${BASE}/faq`, body)).data;

export const updateFaqEntry = async (
  contentKey: string,
  body: UpdateFaqRequest,
): Promise<FaqEntry> =>
  (await apiClient.put<FaqEntry>(`${BASE}/faq/${encodeURIComponent(contentKey)}`, body)).data;

export const deleteFaqEntry = async (
  contentKey: string,
  locale: string = DEFAULT_BOT_MESSAGE_LOCALE,
): Promise<void> => {
  await apiClient.delete(`${BASE}/faq/${encodeURIComponent(contentKey)}`, { params: { locale } });
};
